<template>
  <!-- «Что случилось» (f03, f04): поле 132, фото слева внизу, подсказка и счётчик под полем -->
  <div class="photo-field" :class="{ 'photo-field--disabled': disabled }">
    <div class="photo-field__label">
      Что случилось
    </div>
    <div class="photo-field__box" :class="{ 'photo-field__box--focus': focused }">
      <textarea
        ref="input"
        class="photo-field__input"
        :value="value"
        :maxlength="limit"
        :disabled="disabled"
        :placeholder="disabled ? 'Сначала выберите, с чем нужна помощь' : ''"
        @input="$emit('input', $event.target.value)"
        @focus="focused = true"
        @blur="focused = false"
      />
      <div class="photo-field__photos">
        <button
          type="button"
          class="photo-field__attach"
          aria-label="Приложить фото"
          :disabled="disabled || photos.length >= maxPhotos"
          @click="$refs.file.click()"
        >
          <v-icon size="20">
            mdi-image-plus
          </v-icon>
        </button>
        <input
          ref="file"
          type="file"
          accept="image/jpeg,image/png,image/heic,.heic"
          multiple
          hidden
          @change="addPhotos"
        >
        <div v-for="photo in photos" :key="photo.key" class="photo-field__photo">
          <img :src="photo.preview" alt="">
          <v-progress-circular
            v-if="photo.uploading"
            indeterminate
            size="20"
            width="2"
            class="photo-field__progress"
          />
          <button
            type="button"
            class="photo-field__remove"
            aria-label="Убрать фото"
            @click="removePhoto(photo)"
          >
            <v-icon size="14">
              mdi-close
            </v-icon>
          </button>
        </div>
      </div>
    </div>
    <div class="photo-field__under">
      <span>По возможности прикладывайте фото</span>
      <span>{{ value.length }} / {{ limit }}</span>
    </div>
  </div>
</template>

<script>
  import supportApi, { supportErrorText } from '@/api/support'
  import { TEXT_LIMIT } from '../form'

  const MAX_PHOTOS = 10
  const MAX_PHOTO_BYTES = 10 * 1024 * 1024

  export default {
    name: 'PhotoField',
    props: {
      value: { type: String, default: '' },
      disabled: { type: Boolean, default: false },
    },
    data () {
      return { photos: [], focused: false, limit: TEXT_LIMIT, maxPhotos: MAX_PHOTOS }
    },
    computed: {
      uploading () {
        return this.photos.some(p => p.uploading)
      },
    },
    beforeDestroy () {
      this.photos.forEach(p => URL.revokeObjectURL(p.preview))
    },
    methods: {
      focus () {
        this.$refs.input.focus()
      },
      // Для отправки: только загруженные; на ошибке отправки фото остаются (Q23).
      photoIds () {
        return this.photos.filter(p => p.id).map(p => p.id)
      },
      reset () {
        this.photos.forEach(p => URL.revokeObjectURL(p.preview))
        this.photos = []
      },
      async addPhotos (event) {
        const files = Array.from(event.target.files || [])
        event.target.value = ''
        for (const file of files) {
          if (this.photos.length >= MAX_PHOTOS) {
            this.showWarningMessage(`Не больше ${MAX_PHOTOS} фото в одном обращении`)
            break
          }
          if (file.size > MAX_PHOTO_BYTES) {
            this.showWarningMessage(`«${file.name}» больше 10 МБ`)
            continue
          }
          const photo = { key: `${file.name}-${Date.now()}-${Math.random()}`, id: null, uploading: true, preview: URL.createObjectURL(file) }
          this.photos.push(photo)
          // Загружаем сразу: к «Отправить» id уже готовы.
          try {
            photo.id = (await supportApi.upload(file)).id
          } catch (e) {
            this.showErrorMessage(supportErrorText(e, 'Не удалось загрузить фото'))
            this.removePhoto(photo)
          } finally {
            photo.uploading = false
            this.$emit('uploading', this.uploading)
          }
        }
        this.$emit('photos', this.photos.length)
      },
      removePhoto (photo) {
        URL.revokeObjectURL(photo.preview)
        this.photos = this.photos.filter(p => p !== photo)
        this.$emit('photos', this.photos.length)
      },
    },
  }
</script>

<style scoped lang="less">
@import '../tokens.less';

.photo-field--disabled {
  opacity: .4;
}

.photo-field__label {
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 500;
  color: @fakt-text;
}

.photo-field__box {
  display: flex;
  flex-direction: column;
  min-height: 132px;
  padding: 12px 10px 8px 14px;
  border: 1px solid transparent;
  border-radius: 10px;
  background: @fakt-surface;
  transition: border-color 0.16s ease;

  &--focus {
    border-color: @fakt-orange;
  }
}

.photo-field__input {
  flex: 1;
  min-height: 60px;
  font-size: 14px;
  line-height: 20px;
  resize: none;
  outline: none;
  color: @fakt-text;

  &::placeholder {
    color: @fakt-text-3;
  }
}

.photo-field__photos {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: flex-end;
}

.photo-field__attach {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: @fakt-white;

  .v-icon {
    color: @fakt-orange !important;
  }
}

.photo-field__photo {
  position: relative;
  width: 56px;
  height: 56px;
  overflow: hidden;
  border-radius: 8px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.photo-field__progress {
  position: absolute;
  top: 18px;
  left: 18px;
}

.photo-field__remove {
  position: absolute;
  top: 4px;
  right: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(0, 0, 0, .65);

  .v-icon {
    color: @fakt-white !important;
  }
}

.photo-field__under {
  display: flex;
  justify-content: space-between;
  margin-top: 6px;
  font-size: 12px;
  color: @fakt-text-3;
}
</style>
